import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BellRing,
  Check,
  ChevronDown,
  Download,
  Eye,
  Heart,
  HeartPulse,
  Home,
  Leaf,
  Loader2,
  Pencil,
  Sparkles,
  Sprout,
  Sun,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";
import { WellnessSnapshot } from "@/components/journey/WellnessSnapshot";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const LABEL_STYLE = {
  Thriving: "border-gold/50 bg-gold/10 text-gold-dark",
  "Finding Balance": "border-forest/20 bg-sage text-forest",
  "Out of Balance": "border-[#D8C9A8] bg-[#F1E9D8] text-[#7A6A45]",
  "Survival Mode": "border-forest bg-forest text-ivory",
  "Insufficient Information": "border-charcoal/15 bg-charcoal/5 text-taupe",
};

const DIM_EXPLAIN = {
  Thriving: "Your answers describe steady support here.",
  "Finding Balance": "Your answers describe a workable mix of support and strain.",
  "Out of Balance": "Your answers suggest this area is carrying more strain than support right now.",
  "Survival Mode": "Your answers suggest this area needs the most gentle attention right now.",
  "Insufficient Information": "A few more answers would help describe this area.",
};

const DIM_ORDER = ["PHY", "ENV", "EMO", "SPI"];
const DIM_ICON = { PHY: HeartPulse, ENV: Home, EMO: Heart, SPI: Sun };

const STATUS_OPTIONS = [
  { key: "planning", label: "Planning" },
  { key: "exploring", label: "Exploring" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
  { key: "paused", label: "Paused" },
];
const displayStatus = (s) => (s === "active" ? "in_progress" : s || "planning");

const LeafPips = ({ index }) => (
  <span className="flex items-center gap-1.5" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <Leaf
        key={i}
        className={`h-4 w-4 ${index != null && i < index ? "fill-gold text-gold" : "text-forest/20"}`}
      />
    ))}
  </span>
);

const Expandable = ({ label, testid, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4">
      <button
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-gold-dark transition-colors hover:text-forest"
        aria-expanded={open}
      >
        {label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="pt-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const InsightCard = ({ item, testid, showInterpretation, icon: Icon }) => (
  <div
    data-testid={testid}
    className="rounded-[1.75rem] border border-forest/12 bg-white/70 p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_45px_rgba(23,63,53,0.1)]"
  >
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">
        {item.category_name}
      </p>
      {Icon && (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sage">
          <Icon className="h-4 w-4 text-forest" />
        </span>
      )}
    </div>
    <h3 className="mt-2 font-serif text-xl leading-snug text-forest">{item.title}</h3>
    <p className="mt-3 text-sm leading-relaxed text-charcoal/75">{item.narrative}</p>
    {showInterpretation && item.interpretation && (
      <p className="mt-3 text-sm italic leading-relaxed text-taupe">{item.interpretation}</p>
    )}
    {item.takeaway && (
      <Expandable label="Worth knowing" testid={`${testid}-takeaway-toggle`}>
        <p className="text-sm leading-relaxed text-charcoal/70">{item.takeaway}</p>
      </Expandable>
    )}
  </div>
);

export default function Results() {
  const { assessmentId } = useParams();
  const { member, setMember } = useAuth();
  const [report, setReport] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [intention, setIntention] = useState(null);
  const [intentionDoc, setIntentionDoc] = useState(null);
  const [editingIntention, setEditingIntention] = useState(false);
  const [pickedSuggestion, setPickedSuggestion] = useState(null);
  const [customIntention, setCustomIntention] = useState("");
  const [savingIntention, setSavingIntention] = useState(false);
  const [timeline, setTimeline] = useState(null);

  useEffect(() => {
    document.title = "My Rooted Wellness Profile | Rooted Healing";
    api
      .get(`/assessments/${assessmentId}/report`)
      .then(({ data }) => {
        setReport(data);
        setIntention(data.intention || null);
      })
      .catch((e) =>
        toast.error(formatApiError(e.response?.data?.detail, "Could not load your profile."))
      );
    api
      .get("/intentions")
      .then(({ data }) =>
        setIntentionDoc(
          (data.intentions || []).find((i) => i.assessment_id === assessmentId) || null
        )
      )
      .catch(() => {});
    api
      .get("/assessments/timeline")
      .then(({ data }) => setTimeline(data.entries || []))
      .catch(() => setTimeline([]));
  }, [assessmentId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/assessments/${assessmentId}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "rooted-wellness-profile.pdf";
      a.click();
      URL.revokeObjectURL(url);
      track("report_downloaded");
    } catch {
      toast.error("Could not download your report just now.");
    }
    setDownloading(false);
  };

  const saveIntentionText = async (text) => {
    if (!text || !text.trim()) {
      toast.error("Choose a suggestion or write your own intention first.");
      return;
    }
    setSavingIntention(true);
    try {
      const { data } = await api.put(`/assessments/${assessmentId}/intention`, { text: text.trim() });
      setIntention(data.intention);
      setIntentionDoc((prev) =>
        prev
          ? { ...prev, text: data.intention.text, status: data.intention.status }
          : { intention_id: data.intention.intention_id, status: data.intention.status, assessment_id: assessmentId }
      );
      setEditingIntention(false);
      setPickedSuggestion(null);
      setCustomIntention("");
      toast.success("Your intention is saved to your account.");
      track("intention_saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not save your intention."));
    }
    setSavingIntention(false);
  };

  const saveIntention = async () => {
    const text = pickedSuggestion === "custom" ? customIntention.trim() : pickedSuggestion;
    await saveIntentionText(text || "");
  };

  const setIntentionStatus = async (status) => {
    const id = intentionDoc?.intention_id || intention?.intention_id;
    if (!id) return;
    try {
      await api.patch(`/intentions/${id}`, { status });
      setIntentionDoc((prev) => ({ ...(prev || {}), intention_id: id, status }));
      toast.success(
        status === "completed"
          ? "Marked complete. Wonderful, however it unfolded."
          : "Status updated."
      );
    } catch {
      toast.error("Could not update the status just now.");
    }
  };

  const toggleReminder = async (checked) => {
    try {
      const { data } = await api.patch("/auth/preferences", { consent_reminders: checked });
      setMember(data);
      toast.success(
        checked
          ? "We will send a gentle reminder about 90 and 180 days after this discovery."
          : "Reminders turned off."
      );
    } catch {
      toast.error("Could not save that preference.");
    }
  };

  if (!report) {
    return (
      <main className="flex min-h-screen items-center justify-center pt-[76px]">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </main>
    );
  }

  const r = report.results;
  const entries = r.dimension_entries || {};
  const firstName = (report.member_name || "Friend").split(" ")[0];
  const date = new Date(report.created_at).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const consults = report.consultations;

  const relatedFor = (cat) => ({
    strengths: r.strengths.filter((s) => s.category === cat),
    attention: r.attention.filter((a) => a.category === cat),
  });

  const currentStatus = displayStatus(intentionDoc?.status || intention?.status);
  const discoveryCount = timeline ? timeline.length : null;

  return (
    <main data-testid="results-page" className="pt-[76px]">
      <div className="border-b border-gold/30 bg-sage/70 px-5 py-3.5 text-center sm:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
          {report.approval_status.replace(/[\u2014\u2013]/g, "\u00b7")}
        </p>
      </div>

      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-sage/60 blur-3xl"
        />
        <div className="relative mx-auto max-w-3xl px-5 py-16 text-center sm:px-8 sm:py-20">
          <Reveal>
            <Overline>My Rooted Wellness Profile</Overline>
            <h1
              data-testid="results-hero-heading"
              className="mt-4 font-serif text-4xl leading-tight text-forest sm:text-5xl"
            >
              {firstName}, here's your <em className="italic text-gold">Rooted Wellness Profile</em>
            </h1>
            <p className="mt-3 text-sm text-taupe">Completed {date}</p>
            <div className="mt-7">
              <span
                data-testid="results-overall-label"
                className={`inline-flex rounded-full border px-7 py-2.5 font-serif text-2xl ${LABEL_STYLE[r.overall] || LABEL_STYLE["Insufficient Information"]}`}
              >
                {r.overall === "Insufficient Information" ? "A Gentle Beginning" : r.overall}
              </span>
            </div>
            <div className="mx-auto mt-8 max-w-2xl space-y-5 text-left">
              {(r.overall_narrative || []).map((p, i) => (
                <p
                  key={i}
                  data-testid={`results-narrative-${i}`}
                  className="text-lg font-light leading-relaxed text-charcoal/75"
                >
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 rounded-[2.5rem] border border-forest/10 bg-white/75 p-8 shadow-[0_20px_50px_rgba(23,63,53,0.1)] sm:p-10">
              <h2 className="font-serif text-xl text-forest">Your Wellness Snapshot</h2>
              <div className="mt-6">
                <WellnessSnapshot dimensions={r.dimensions} />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <button
              data-testid="results-download-pdf"
              onClick={downloadPdf}
              disabled={downloading}
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:shadow-[0_14px_35px_rgba(23,63,53,0.28)] disabled:opacity-50"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Download My Report (PDF)
            </button>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 sm:px-8" data-testid="results-dimensions">
        <Reveal>
          <h2 className="font-serif text-2xl text-forest sm:text-3xl">Your Five Dimensions</h2>
          <p className="mt-2 text-sm text-charcoal/65">
            Each card reflects how you described that part of your life. Tap any card to
            privately review the answers behind it.
          </p>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {DIM_ORDER.map((cat, i) => {
            const d = r.dimensions[cat];
            const rel = relatedFor(cat);
            const Icon = DIM_ICON[cat];
            return (
              <Reveal key={cat} delay={i * 0.07}>
                <div
                  data-testid={`results-dimension-${cat.toLowerCase()}`}
                  className="flex h-full flex-col rounded-[2rem] border border-forest/12 bg-white/70 p-8"
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage">
                      <Icon className="h-5 w-5 text-forest" />
                    </span>
                    <LeafPips index={d.index} />
                  </div>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-taupe">
                    {report.category_names[cat]}
                  </p>
                  <span
                    className={`mt-3 inline-flex w-fit rounded-full border px-4 py-1.5 font-serif text-lg ${LABEL_STYLE[d.label] || LABEL_STYLE["Insufficient Information"]}`}
                  >
                    {d.label}
                  </span>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
                    {DIM_EXPLAIN[d.label] || DIM_EXPLAIN["Insufficient Information"]}
                  </p>
                  {(rel.strengths.length > 0 || rel.attention.length > 0) && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {rel.strengths.map((s) => (
                        <span
                          key={s.answer_id}
                          className="rounded-full bg-gold/12 px-3 py-1 text-[11px] font-semibold text-gold-dark"
                        >
                          Supporting: {s.title}
                        </span>
                      ))}
                      {rel.attention.map((a) => (
                        <span
                          key={a.answer_id}
                          className="rounded-full bg-forest/8 px-3 py-1 text-[11px] font-semibold text-forest"
                        >
                          Worth attention: {a.title}
                        </span>
                      ))}
                    </div>
                  )}
                  {(entries[cat] || []).length > 0 && (
                    <Expandable
                      label="Review my answers"
                      testid={`dimension-expand-${cat.toLowerCase()}`}
                    >
                      <ul className="flex flex-col gap-3">
                        {entries[cat].map((e) => (
                          <li key={e.answer_id} className="border-l-2 border-gold/40 pl-4">
                            <p className="text-xs text-taupe">{e.question_text}</p>
                            <p className="text-sm font-semibold text-forest">{e.answer_text}</p>
                          </li>
                        ))}
                      </ul>
                    </Expandable>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.1}>
          <div
            data-testid="results-dimension-inh"
            className="mt-5 rounded-[2rem] border border-gold/35 bg-sage/60 p-8"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
              {report.category_names.INH}
            </p>
            <h3 className="mt-2 font-serif text-2xl text-forest">Your Contextual Profile</h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-charcoal/70">
              Family and personal history shape the landscape you began with. They are shared
              here as context for your story and for conversations with Staci, never as a grade
              or a verdict about your future.
            </p>
            {report.inheritance_context.length > 0 && (
              <Expandable label="Review my answers" testid="dimension-expand-inh">
                <ul className="flex flex-col gap-3">
                  {report.inheritance_context.map((item) => (
                    <li key={item.question} className="border-l-2 border-gold/40 pl-4">
                      <p className="text-xs text-taupe">{item.question}</p>
                      <p className="text-sm font-semibold text-forest">{item.answer}</p>
                    </li>
                  ))}
                </ul>
              </Expandable>
            )}
          </div>
        </Reveal>
      </section>

      {r.strengths.length > 0 && (
        <section className="mx-auto max-w-5xl px-5 pt-16 sm:px-8" data-testid="results-strengths">
          <Reveal>
            <Overline>What's Supporting You</Overline>
            <h2 className="font-serif text-2xl text-forest sm:text-3xl">
              The roots already holding you
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {r.strengths.map((s, i) => (
              <Reveal key={s.answer_id} delay={i * 0.07}>
                <InsightCard
                  item={s}
                  testid={`results-strength-${s.answer_id.toLowerCase()}`}
                  icon={Sprout}
                />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {r.attention.length > 0 && (
        <section className="mx-auto max-w-5xl px-5 pt-16 sm:px-8" data-testid="results-attention">
          <Reveal>
            <Overline>What's Asking for Attention</Overline>
            <h2 className="font-serif text-2xl text-forest sm:text-3xl">
              Where gentle care could go next
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {r.attention.map((a, i) => (
              <Reveal key={a.answer_id} delay={i * 0.07}>
                <InsightCard
                  item={a}
                  testid={`results-attention-${a.answer_id.toLowerCase()}`}
                  showInterpretation
                  icon={Leaf}
                />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {r.pattern_details.length > 0 && (
        <section className="mx-auto max-w-5xl px-5 pt-16 sm:px-8" data-testid="results-patterns">
          <Reveal>
            <Overline>Connecting the Dots</Overline>
            <h2 className="font-serif text-2xl text-forest sm:text-3xl">
              Patterns across your answers
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-charcoal/65">
              These patterns show experiences you reported side by side. Sharing a pattern does
              not mean one experience caused the other.
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {r.pattern_details.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.07}>
                <div
                  data-testid={`results-pattern-${p.id.toLowerCase()}`}
                  className="flex h-full flex-col rounded-[2rem] border border-gold/30 bg-gradient-to-br from-sage/70 to-white/70 p-8"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-gold-dark" />
                    <div className="flex flex-wrap gap-2">
                      {p.dimensions.map((d) => (
                        <span
                          key={d}
                          className="rounded-full bg-forest/8 px-3 py-1 text-[11px] font-semibold text-forest"
                        >
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                  <h3 className="mt-4 font-serif text-xl text-forest">{p.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/75">{p.interpretation}</p>
                  <p className="mt-4 border-l-2 border-gold/50 pl-4 font-serif text-sm italic leading-relaxed text-forest/85">
                    {p.reflection}
                  </p>
                  {(p.education || p.professional_support) && (
                    <Expandable label="Worth knowing" testid={`pattern-expand-${p.id.toLowerCase()}`}>
                      {p.education && (
                        <p className="text-sm leading-relaxed text-charcoal/70">{p.education}</p>
                      )}
                      {p.professional_support && (
                        <p className="mt-2 text-sm leading-relaxed text-charcoal/70">
                          {p.professional_support}
                        </p>
                      )}
                    </Expandable>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {r.starting_points.length > 0 && (
        <section className="mx-auto max-w-3xl px-5 pt-16 sm:px-8" data-testid="results-starting-points">
          <Reveal>
            <Overline>Where to Begin</Overline>
            <h2 className="font-serif text-2xl text-forest sm:text-3xl">Small first steps</h2>
            <p className="mt-2 text-sm text-charcoal/65">
              Gentle, manageable ideas drawn from what you shared. They are educational
              suggestions, never prescribed treatments.
            </p>
          </Reveal>
          <div className="mt-8 flex flex-col gap-4">
            {r.starting_points.map((sp, i) => {
              const suggestionText = (r.intention_suggestions || [])[i] || sp;
              const isSaved = intention?.text === suggestionText;
              const why = r.pattern_details[i]?.name;
              return (
                <Reveal key={sp} delay={i * 0.06}>
                  <div
                    data-testid={`results-starting-point-${i}`}
                    className="rounded-[1.75rem] border border-forest/12 bg-white/70 p-6 transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(23,63,53,0.1)]"
                  >
                    <div className="flex items-start gap-4">
                      <span className="font-serif text-2xl text-gold">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm leading-relaxed text-charcoal/85">{sp}</p>
                        {why && (
                          <p className="mt-2 text-xs leading-relaxed text-taupe">
                            Suggested because of the pattern we noticed: {why}.
                          </p>
                        )}
                        <button
                          data-testid={`starting-point-intention-${i}`}
                          onClick={() => saveIntentionText(suggestionText)}
                          disabled={savingIntention || isSaved}
                          className={`mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold transition-all duration-300 ${
                            isSaved
                              ? "cursor-default bg-gold/15 text-gold-dark"
                              : "border border-forest/25 text-forest hover:border-gold hover:text-gold-dark"
                          } disabled:opacity-60`}
                        >
                          {isSaved ? <Check className="h-3.5 w-3.5" /> : <Sprout className="h-3.5 w-3.5" />}
                          {isSaved ? "Saved as your intention" : "Set as my 90-day intention"}
                        </button>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-3xl px-5 pt-16 sm:px-8" data-testid="results-intention-section">
        <Reveal>
          <div className="rounded-[2.5rem] bg-forest p-8 sm:p-11">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-soft">
              Your 90-Day Wellness Intention
            </p>
            {intention && !editingIntention ? (
              <div data-testid="intention-saved-card">
                <p className="mt-5 font-serif text-xl italic leading-relaxed text-ivory">
                  "{intention.text}"
                </p>
                <p className="mt-4 text-sm text-ivory/60">
                  Saved to your account. We will gently revisit it together at future check-ins.
                  Completing it is a personal note, never a medical outcome.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((o) => (
                    <button
                      key={o.key}
                      data-testid={`intention-status-${o.key}`}
                      onClick={() => setIntentionStatus(o.key)}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                        currentStatus === o.key
                          ? "border-gold-soft bg-gold/25 text-ivory"
                          : "border-ivory/25 text-ivory/65 hover:border-gold-soft/60 hover:text-ivory"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="intention-edit-button"
                  onClick={() => setEditingIntention(true)}
                  className="mt-6 inline-flex items-center gap-2 rounded-full border border-ivory/30 px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:border-gold-soft hover:text-gold-soft"
                >
                  <Pencil className="h-4 w-4" />
                  Update my intention
                </button>
              </div>
            ) : (
              <div>
                <p className="mt-4 text-sm leading-relaxed text-ivory/70">
                  Choose one intention to carry with you, or write your own. This is a personal
                  focus, not a treatment plan.
                </p>
                <div className="mt-6 flex flex-col gap-3">
                  {(r.intention_suggestions || []).map((s, i) => (
                    <button
                      key={i}
                      data-testid={`intention-suggestion-${i}`}
                      onClick={() => setPickedSuggestion(s)}
                      className={`rounded-2xl border px-5 py-4 text-left font-serif text-sm italic leading-relaxed transition-all duration-300 ${
                        pickedSuggestion === s
                          ? "border-gold-soft bg-ivory/10 text-ivory"
                          : "border-ivory/20 text-ivory/75 hover:border-gold-soft/60"
                      }`}
                    >
                      "{s}"
                    </button>
                  ))}
                  <button
                    data-testid="intention-custom-toggle"
                    onClick={() => setPickedSuggestion("custom")}
                    className={`rounded-2xl border px-5 py-4 text-left text-sm transition-all duration-300 ${
                      pickedSuggestion === "custom"
                        ? "border-gold-soft bg-ivory/10 text-ivory"
                        : "border-ivory/20 text-ivory/75 hover:border-gold-soft/60"
                    }`}
                  >
                    Write my own intention...
                  </button>
                  {pickedSuggestion === "custom" && (
                    <textarea
                      data-testid="intention-custom-input"
                      value={customIntention}
                      onChange={(e) => setCustomIntention(e.target.value)}
                      rows={3}
                      maxLength={500}
                      placeholder="Over the next 90 days, I want to..."
                      className="w-full rounded-2xl border border-ivory/25 bg-ivory/10 px-5 py-4 text-sm text-ivory placeholder:text-ivory/40 outline-none focus:border-gold-soft"
                    />
                  )}
                </div>
                <button
                  data-testid="intention-save-button"
                  onClick={saveIntention}
                  disabled={savingIntention}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-forest-deep transition-all duration-300 hover:bg-gold-soft disabled:opacity-50"
                >
                  {savingIntention ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Save My Intention
                </button>
              </div>
            )}
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-3xl px-5 pt-16 sm:px-8" data-testid="results-timeline-intro">
        <Reveal>
          <div className="rounded-[2.5rem] border border-forest/10 bg-white/75 p-8 shadow-[0_16px_40px_rgba(23,63,53,0.08)] sm:p-11">
            <Overline>Your Wellness Timeline Starts Here</Overline>
            <p className="mt-4 leading-relaxed text-charcoal/75">
              This discovery is a snapshot of your experiences today. As your life evolves, you
              can return, revisit your answers, and explore what has changed.
            </p>
            {discoveryCount !== null && discoveryCount <= 1 ? (
              <p className="mt-3 text-sm leading-relaxed text-taupe">
                This is your first saved discovery. Each future one adds a point to your
                personal timeline, creating visual comparisons you can explore whenever you
                wish. There is no schedule to keep.
              </p>
            ) : (
              <div className="mt-5">
                {timeline && timeline.length > 1 && (
                  <p className="text-sm leading-relaxed text-charcoal/70">
                    You now have {timeline.length} saved discoveries. Your journey has moved
                    from{" "}
                    <span className="font-semibold text-forest">{timeline[0].overall}</span> to{" "}
                    <span className="font-semibold text-forest">
                      {timeline[timeline.length - 1].overall}
                    </span>
                    .
                  </p>
                )}
                <Link
                  to="/my-wellness-journey"
                  data-testid="results-view-timeline"
                  className="group mt-5 inline-flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 text-sm font-semibold text-forest transition-all duration-300 hover:border-gold hover:text-gold-dark"
                >
                  View My Wellness Timeline
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            )}
            <label className="mt-7 flex cursor-pointer items-start gap-3 border-t border-forest/10 pt-6 text-sm text-charcoal/75">
              <input
                type="checkbox"
                data-testid="results-reminder-toggle"
                checked={Boolean(member?.consents?.reminders)}
                onChange={(e) => toggleReminder(e.target.checked)}
                className="brand-check mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
              />
              <span className="inline-flex items-start gap-2">
                <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" />
                Remind me to revisit in about 90 days. You can change this anytime in your
                account preferences.
              </span>
            </label>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-20" data-testid="results-consultations">
        <Reveal className="text-center">
          <Overline>What Comes Next</Overline>
          <h2 className="font-serif text-3xl leading-snug text-forest sm:text-4xl">
            You've Discovered Your Starting Point.{" "}
            <em className="italic text-gold">What Comes Next?</em>
          </h2>
          <p className="mx-auto mt-5 max-w-xl leading-relaxed text-charcoal/70">
            Whatever your results showed, a personal conversation with Staci gives you space to
            explore the observations and questions your discovery raised, at your pace, with a
            calm and experienced guide.
          </p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {[
            { key: "terrain", data: consults.terrain },
            { key: "full", data: consults.full },
          ].map(({ key, data: c }, i) => (
            <Reveal key={key} delay={i * 0.08}>
              <div
                data-testid={`results-consultation-${key}`}
                className="flex h-full flex-col rounded-[2rem] border border-forest/12 bg-white/70 p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(23,63,53,0.14)]"
              >
                <div className="flex items-center gap-2 text-gold-dark">
                  <Eye className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em]">
                    {c.minutes} minutes{c.iridology ? " · with iridology" : ""}
                  </span>
                </div>
                <p className="mt-4 flex-1 leading-relaxed text-charcoal/80">{c.cta}</p>
                <p className="mt-3 font-serif text-3xl text-forest">${c.price_usd}</p>
                <a
                  href={consults.booking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`results-book-${key}`}
                  onClick={() => track("booking_click", { location: `results-${key}` })}
                  className="group mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-forest px-6 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep"
                >
                  Book via the Online Scheduler
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-taupe">
          Both options are entirely optional. Iridology is a traditional interpretive practice
          and is not a validated medical diagnostic test.
        </p>
      </section>

      <section className="border-t border-forest/10 bg-sage/40">
        <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8">
          <p className="text-sm leading-relaxed text-taupe">{report.disclaimer}</p>
          <p className="mt-3 text-sm leading-relaxed text-taupe">{report.safety_note}</p>
          <Link
            to="/my-wellness-journey"
            data-testid="results-back-to-dashboard"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-forest underline-offset-4 hover:underline"
          >
            Back to My Wellness Journey
          </Link>
        </div>
      </section>
    </main>
  );
}
